import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  Chip, 
  CircularProgress, 
  Alert,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  Badge,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Search, 
  FilterList, 
  Image as ImageIcon, 
  DeviceHub, 
  LocalFireDepartment, 
  LocalOffer,
  Store,
  Public,
  Refresh,
  ViewModule,
  ViewList
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './constants';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { getDefaultCountryForProject, getDefaultManufacturerRetailers } from './utils/brandBoardUtils';
import { buildMatchingGroupsMapFromTables, groupProductsByMatching } from './matchingUtils';
import { mergeJtiProductRowOverrides } from './productUtils';

// Brand Board Summary - Compact, country-by-country view
function BrandBoardSummaryView() {
  const { projectKey } = useParams();
  
  // State for data
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for country selection
  const [selectedCountry, setSelectedCountry] = useState('ro'); // Default to Romania
  const [availableCountries, setAvailableCountries] = useState([]);
  
  // State for filters
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: null,
    end: null
  });
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for currency conversion
  const [convertToEUR, setConvertToEUR] = useState(true);
  
  // State for view mode
  const [viewMode, setViewMode] = useState('compact'); // 'compact' or 'detailed'
  
  // State for active tab
  const [activeTab, setActiveTab] = useState(0); // 0: Devices, 1: Starter Kits, 2: Consumables
  
  // State for unique filter options
  const [filterOptions, setFilterOptions] = useState({
    retailers: [],
    brands: []
  });

  // Product matching groups (from API, synced on dataset load)
  const [matchingGroups, setMatchingGroups] = useState(new Map());
  
  // State for product detail dialog
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetailDialogOpen, setProductDetailDialogOpen] = useState(false);

  const defaultManufacturerRetailersAppliedRef = useRef(false);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (defaultManufacturerRetailersAppliedRef.current || selectedRetailers.length > 0) return;
    const r = filterOptions.retailers || [];
    if (!r.length) return;
    const mfr = getDefaultManufacturerRetailers(r);
    defaultManufacturerRetailersAppliedRef.current = true;
    if (mfr.length > 0) setSelectedRetailers(mfr);
  }, [filterOptions.retailers, selectedRetailers.length]);
  
  // Apply filters whenever filter state changes
  useEffect(() => {
    applyFilters();
  }, [data, selectedCountry, selectedRetailers, selectedBrands, dateRange, searchTerm]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      const [dataResponse, overridesResponse, brandOverridesResponse, matchingResponse] = await Promise.all([
        axios.get(`${API_URL}/jti/global-merged-data`),
        axios.get(`${API_URL}/jti/product-category-overrides`).catch(() => ({ data: { overrides: {} } })),
        axios.get(`${API_URL}/jti/product-brand-overrides`).catch(() => ({ data: { overrides: {} } })),
        axios.get(`${API_URL}/jti/matching-groups`).catch(() => ({ data: { tables: {} } })),
      ]);
      
      if (dataResponse.data && dataResponse.data.data) {
        const rawData = dataResponse.data.data;
        const columns = dataResponse.data.columns;
        
        // Convert array of arrays to array of objects
        const formattedData = rawData.map(row => {
          const obj = {};
          columns.forEach((col, index) => {
            obj[col] = row[index];
          });
          return obj;
        });

        const overrides = overridesResponse?.data?.overrides || {};
        const brandOverrides = brandOverridesResponse?.data?.overrides || {};
        const formattedWithOverrides = formattedData.map((item) =>
          mergeJtiProductRowOverrides(item, overrides, brandOverrides)
        );
        
        setData(formattedWithOverrides);
        setMatchingGroups(buildMatchingGroupsMapFromTables(matchingResponse?.data?.tables || {}));
        
        // Extract unique filter options
        extractFilterOptions(formattedWithOverrides);

        // Set full available date range by default
        const dates = formattedWithOverrides
          .map((item) => item.updated_at || item.date)
          .filter(Boolean)
          .map((value) => dayjs(value))
          .filter((value) => value.isValid());

        if (dates.length > 0) {
          const minDate = dates.reduce((min, current) => (current.isBefore(min) ? current : min), dates[0]);
          const maxDate = dates.reduce((max, current) => (current.isAfter(max) ? current : max), dates[0]);
          setDateRange({ start: minDate, end: maxDate });
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load brand board summary data');
    } finally {
      setLoading(false);
    }
  };
  
  const extractFilterOptions = (data) => {
    const countries = [...new Set(data.map(item => item.country).filter(Boolean))];
    const retailers = [...new Set(data.map(item => item.retailer).filter(Boolean))];
    const brands = [...new Set(data.map(item => item.brand).filter(Boolean))];
    
    setAvailableCountries(countries);
    setFilterOptions({
      retailers,
      brands
    });
    const defaultCountry = getDefaultCountryForProject(projectKey, countries);
    if (defaultCountry) {
      setSelectedCountry(defaultCountry);
    } else if (countries.length > 0 && !countries.includes(selectedCountry)) {
      setSelectedCountry(countries[0]);
    }
  };
  
  const applyFilters = () => {
    let filtered = [...data];
    
    // Filter by country
    if (selectedCountry) {
      filtered = filtered.filter(item => item.country === selectedCountry);
    }
    
    // Filter by retailers
    if (selectedRetailers.length > 0) {
      filtered = filtered.filter(item => selectedRetailers.includes(item.retailer));
    }
    
    // Filter by brands
    if (selectedBrands.length > 0) {
      filtered = filtered.filter(item => selectedBrands.includes(item.brand));
    }
    
    // Filter by date range
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter(item => {
        const rawDate = item.updated_at || item.date;
        if (!rawDate) return true;
        const itemDate = dayjs(rawDate);
        if (!itemDate.isValid()) return true;
        return itemDate.isAfter(dateRange.start.subtract(1, 'day')) &&
               itemDate.isBefore(dateRange.end.add(1, 'day'));
      });
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item => 
        (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.brand && item.brand.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    setFilteredData(filtered);
  };
  
  // Currency conversion function
  const convertPrice = (price, currency) => {
    if (!price || isNaN(parseFloat(price))) return { price: 0, currency: 'EUR' };
    
    const numericPrice = parseFloat(price);
    
    if (convertToEUR) {
      if (currency === 'RON') {
        return {
          price: (numericPrice / 5.09).toFixed(2),
          currency: 'EUR'
        };
      } else if (currency === 'GBP') {
        return {
          price: (numericPrice * 1.15).toFixed(2),
          currency: 'EUR'
        };
      }
    }
    
    return {
      price: numericPrice.toFixed(2),
      currency: currency || 'EUR'
    };
  };
  
  // Product deduplication function - keeps one representative item per product (ignoring flavor variations)
  const deduplicateProducts = (products) => {
    const productMap = new Map();
    
    products.forEach(product => {
      // Create a base key that ignores flavor variations
      const baseTitle = (product.title || '').toLowerCase()
        .replace(/\s+(menthol|mint|tobacco|original|classic|smooth|fresh|cool|warm|hot|mild|strong|light|dark|black|white|red|blue|green|yellow|orange|purple|pink|brown|grey|gray|silver|gold|platinum|premium|deluxe|special|limited|edition|flavor|taste|aroma|scent|smell|fragrance)\s*/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      
      const brand = (product.brand || '').toLowerCase().trim();
      const retailer = (product.retailer || '').toLowerCase().trim();
      const category = (product.category_group || product.clean_category || '').toLowerCase().trim();
      
      // Create key that ignores flavor variations
      const key = `${baseTitle}_${brand}_${retailer}_${category}`;
      
      if (!productMap.has(key)) {
        productMap.set(key, product);
      } else {
        // Keep the one with the latest date or higher price
        const existingProduct = productMap.get(key);
        const existingDate = new Date(existingProduct.updated_at || existingProduct.date || 0);
        const currentDate = new Date(product.updated_at || product.date || 0);
        
        // If dates are the same, keep the one with higher price
        if (currentDate.getTime() === existingDate.getTime()) {
          const existingPrice = parseFloat(convertPrice(existingProduct.actual_price || existingProduct.regular_price, existingProduct.currency).price);
          const currentPrice = parseFloat(convertPrice(product.actual_price || product.regular_price, product.currency).price);
          if (currentPrice > existingPrice) {
            productMap.set(key, product);
          }
        } else if (currentDate > existingDate) {
          productMap.set(key, product);
        }
      }
    });
    
    return Array.from(productMap.values());
  };
  
  // Group products by category - uses category_group (with overrides) to match JTIPrice view
  const groupProductsByCategory = (products) => {
    const deduplicatedProducts = deduplicateProducts(products);
    
    const categories = {
      'Devices': [],
      'Starter Kits': [],
      'Sticks': [],
      'Sticks Bundles': [],
      'Consumables': []
    };
    
    deduplicatedProducts.forEach(product => {
      // Use category_group only (already has user recategorization overrides applied in loadData).
      // No heuristics - starter kits and other categories are based purely on user recategorization / API data.
      const categoryGroup = (product.category_group || '').trim();
      
      if (categoryGroup === 'Devices' || categoryGroup === 'Device Bundles') {
        categories['Devices'].push(product);
      } else if (categoryGroup === 'Starter Kits' || categoryGroup === 'Offers') {
        categories['Starter Kits'].push(product);
      } else if (categoryGroup === 'Sticks') {
        categories['Sticks'].push(product);
      } else if (categoryGroup === 'Sticks Bundles') {
        categories['Sticks Bundles'].push(product);
      } else if (categoryGroup === 'Consumables' || categoryGroup === 'Other' || categoryGroup === 'Accessories' || !categoryGroup) {
        categories['Consumables'].push(product);
      } else {
        categories['Consumables'].push(product);
      }
    });
    
    // Sort each category by price (high to low)
    Object.keys(categories).forEach(category => {
      categories[category].sort((a, b) => {
        const priceA = parseFloat(convertPrice(a.actual_price || a.regular_price, a.currency).price);
        const priceB = parseFloat(convertPrice(b.actual_price || b.regular_price, b.currency).price);
        return priceB - priceA;
      });
    });
    
    return categories;
  };
  
  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setProductDetailDialogOpen(true);
  };
  
  const renderCompactProductCard = (product, index) => {
    const hasImage = product.image && product.image !== 'null' && product.image !== '';
    const priceData = convertPrice(product.actual_price || product.regular_price, product.currency);
    const regularPriceData = convertPrice(product.regular_price, product.currency);
    const isOnSale = product.regular_price && product.actual_price && 
                    parseFloat(regularPriceData.price) !== parseFloat(priceData.price);
    
    return (
      <Card 
        key={index}
        onClick={() => handleProductClick(product)}
        sx={{ 
          cursor: 'pointer',
          backgroundColor: 'transparent',
          border: '1px solid #e0e0e0',
          transition: 'all 0.2s ease-in-out',
          '&:hover': { 
            boxShadow: 4,
            transform: 'translateY(-2px)',
            backgroundColor: 'rgba(0, 0, 0, 0.02)'
          }
        }}
      >
        <CardContent sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Product Image */}
            <Box 
              sx={{ 
                width: 60, 
                height: 60, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                flexShrink: 0
              }}
            >
              {hasImage ? (
                <img 
                  src={product.image} 
                  alt={product.title}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '100%', 
                    objectFit: 'contain' 
                  }}
                />
              ) : (
                <ImageIcon sx={{ color: '#666', fontSize: 24 }} />
              )}
            </Box>

            {/* Product Info */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 0.5,
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word'
                }}
              >
                {product.title || 'Untitled Product'}
              </Typography>
              
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                {product.brand} • {product.retailer}
              </Typography>

              {/* Price */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography 
                  variant="body2" 
                  color="primary" 
                  sx={{ fontWeight: 'bold' }}
                >
                  {priceData.price} {priceData.currency}
                </Typography>
                {isOnSale && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ textDecoration: 'line-through' }}
                  >
                    {regularPriceData.price}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };
  
  const renderDetailedProductCard = (product, index) => {
    const hasImage = product.image && product.image !== 'null' && product.image !== '';
    const priceData = convertPrice(product.actual_price || product.regular_price, product.currency);
    const regularPriceData = convertPrice(product.regular_price, product.currency);
    const isOnSale = product.regular_price && product.actual_price && 
                    parseFloat(regularPriceData.price) !== parseFloat(priceData.price);
    
    return (
      <Card 
        key={index}
        onClick={() => handleProductClick(product)}
        sx={{ 
          cursor: 'pointer',
          backgroundColor: 'transparent',
          border: '1px solid #e0e0e0',
          transition: 'all 0.2s ease-in-out',
          '&:hover': { 
            boxShadow: 4,
            transform: 'translateY(-2px)',
            backgroundColor: 'rgba(0, 0, 0, 0.02)'
          }
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {/* Product Image */}
            <Box 
              sx={{ 
                width: 80, 
                height: 80, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                flexShrink: 0
              }}
            >
              {hasImage ? (
                <img 
                  src={product.image} 
                  alt={product.title}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '100%', 
                    objectFit: 'contain' 
                  }}
                />
              ) : (
                <ImageIcon sx={{ color: '#666', fontSize: 32 }} />
              )}
            </Box>

            {/* Product Info */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 1,
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word'
                }}
              >
                {product.title || 'Untitled Product'}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Chip label={product.brand} size="small" color="primary" variant="outlined" />
                <Chip label={product.retailer} size="small" color="secondary" variant="outlined" />
                {product.variant && <Chip label={product.variant} size="small" />}
              </Box>

              {/* Price */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography 
                  variant="h6" 
                  color="primary" 
                  sx={{ fontWeight: 'bold' }}
                >
                  {priceData.price} {priceData.currency}
                </Typography>
                {isOnSale && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ textDecoration: 'line-through' }}
                  >
                    {regularPriceData.price} {regularPriceData.currency}
                  </Typography>
                )}
              </Box>

              {/* Stock Status */}
              {product.stock_status && (
                <Chip 
                  label={product.stock_status} 
                  size="small"
                  color={product.stock_status === 'In Stock' ? 'success' : 'error'}
                />
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };
  
  const renderProductGroups = () => {
    // Apply matching groups to collapse products in same group (when prices match)
    const dataWithGroups = matchingGroups.size > 0
      ? groupProductsByMatching(filteredData, matchingGroups)
      : filteredData;
    const groupedProducts = groupProductsByCategory(dataWithGroups);
    
    // Define tab categories in order (Sticks and Sticks Bundles are separate, like JTIPrice view)
    const tabCategories = ['Devices', 'Starter Kits', 'Sticks', 'Sticks Bundles', 'Consumables'];
    const tabLabels = ['Devices', 'Starter Kits', 'Sticks', 'Sticks Bundles', 'Consumables'];
    const tabIcons = [<DeviceHub />, <LocalOffer />, <LocalFireDepartment />, <LocalFireDepartment />, <LocalFireDepartment />];
    const tabColors = ['#e5e5e5', '#e5e5e5', '#e5e5e5', '#e5e5e5', '#e5e5e5'];
    
    const handleTabChange = (event, newValue) => {
      setActiveTab(newValue);
    };
    
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Tabs */}
        <Paper sx={{ mb: 1 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ 
              '& .MuiTab-root': { 
                minHeight: 48,
                fontSize: '0.8rem',
                fontWeight: 'bold'
              }
            }}
          >
            {tabCategories.map((category, index) => {
              const products = groupedProducts[category] || [];
              return (
                <Tab 
                  key={category}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {tabIcons[index]}
                      <span>{tabLabels[index]}</span>
                      <Badge 
                        badgeContent={products.length} 
                        color="secondary"
                        sx={{ ml: 0.5, fontSize: '0.6rem' }}
                      />
                    </Box>
                  }
                  sx={{
                    backgroundColor: activeTab === index ? tabColors[index] : 'transparent',
                    color: activeTab === index ? 'white' : 'text.primary',
                    '&:hover': {
                      backgroundColor: activeTab === index ? tabColors[index] : 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                />
              );
            })}
          </Tabs>
        </Paper>
        
        {/* Tab Content */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {tabCategories.map((category, index) => {
            if (activeTab !== index) return null;
            
            const products = groupedProducts[category] || [];
            
            if (products.length === 0) {
              return (
                <Box key={category} sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: '#fafafa'
                }}>
                  <Typography variant="h6" color="text.secondary">
                    No {category.toLowerCase()} found
                  </Typography>
                </Box>
              );
            }
            
            // Create brand board layout for this category
            const brandBoardLayout = createBrandBoardLayout(products, category);
            
            return (
              <Box key={category} sx={{ height: '100%', overflow: 'hidden' }}>
                {renderBrandBoardLayout(brandBoardLayout, category)}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };
  
  // Create brand board layout with price tiers and brand columns
  const createBrandBoardLayout = (products, category) => {
    const deduplicatedProducts = deduplicateProducts(products);
    
    // Convert all prices to EUR for consistent comparison
    const productsWithConvertedPrices = deduplicatedProducts.map(product => {
      const priceData = convertPrice(product.actual_price || product.regular_price, product.currency);
      return {
        ...product,
        convertedPrice: parseFloat(priceData.price),
        displayPrice: priceData.price,
        displayCurrency: priceData.currency
      };
    });
    
    // Find category-specific reference product (100% index)
    let referenceProduct = null;
    let referencePrice = 5.0; // Default fallback
    
    if (category === 'Devices') {
      referenceProduct = productsWithConvertedPrices.find(p => 
        p.title && p.title.toLowerCase().includes('promo kit iqos iluma i one cu terea')
      );
    } else if (category === 'Starter Kits') {
      referenceProduct = productsWithConvertedPrices.find(p => 
        p.title && (p.title.toLowerCase().includes('starter') || p.title.toLowerCase().includes('kit'))
      );
    } else if (category === 'Sticks' || category === 'Sticks Bundles' || category === 'Consumables') {
      referenceProduct = productsWithConvertedPrices.find(p => 
        p.title && (p.title.toLowerCase().includes('terea') || p.title.toLowerCase().includes('fiit'))
      );
    }
    
    // If no specific reference found, use the first product as fallback
    if (!referenceProduct && productsWithConvertedPrices.length > 0) {
      referenceProduct = productsWithConvertedPrices[0];
    }
    
    if (referenceProduct) {
      referencePrice = referenceProduct.convertedPrice;
    }
    
    // Calculate price indices relative to category-specific reference (100%)
    const productsWithPriceIndex = productsWithConvertedPrices.map(product => {
      const priceIndex = Math.round((product.convertedPrice / referencePrice) * 100);
      return {
        ...product,
        priceIndex,
        displayPriceIndex: `${priceIndex}%`
      };
    });
    
    // Group products by price index ranges
    const priceIndexTolerance = 5; // 5% tolerance for grouping similar price indices
    const priceGroups = [];
    
    // Sort all products by price index (high to low)
    productsWithPriceIndex.sort((a, b) => b.priceIndex - a.priceIndex);
    
    productsWithPriceIndex.forEach(product => {
      // Find existing price group within tolerance
      let addedToGroup = false;
      for (let group of priceGroups) {
        const groupAvgIndex = group.products.reduce((sum, p) => sum + p.priceIndex, 0) / group.products.length;
        if (Math.abs(product.priceIndex - groupAvgIndex) <= priceIndexTolerance) {
          group.products.push(product);
          addedToGroup = true;
          break;
        }
      }
      
      // Create new price group if no suitable group found
      if (!addedToGroup) {
        priceGroups.push({
          priceIndex: product.priceIndex,
          priceRange: product.convertedPrice,
          products: [product]
        });
      }
    });
    
    // Get all unique brands
    const allBrands = [...new Set(productsWithPriceIndex.map(p => p.brand))];
    
    return {
      priceGroups,
      allBrands,
      referencePrice,
      referenceProduct: referenceProduct,
      category: category
    };
  };
  
  // Render brand board layout similar to reference images - ULTRA COMPACT
  const renderBrandBoardLayout = (layout, category) => {
    const { priceGroups, allBrands, referenceProduct, category: layoutCategory } = layout;
    
    if (allBrands.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No products found for {category}
          </Typography>
        </Box>
      );
    }
    
    return (
      <Paper sx={{ p: 1.5, backgroundColor: '#fafafa', maxHeight: '80vh', overflow: 'auto' }}>
        {/* Reference Indicator */}
        {referenceProduct && (
          <Box sx={{ mb: 1, p: 0.5, backgroundColor: '#fff3e0', borderRadius: 0.5, border: '1px solid #ff9800' }}>
            <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 'bold', color: '#e65100' }}>
              📊 {category} Price Index: {referenceProduct.title} = 100% (base price: €{referenceProduct.convertedPrice.toFixed(2)})
            </Typography>
          </Box>
        )}
        
        {/* Brand Headers - Ultra Compact with Manufacturer Logos */}
        <Box sx={{ display: 'flex', mb: 1 }}>
          <Box sx={{ width: 60, flexShrink: 0 }} /> {/* Space for price labels */}
          {allBrands.map(brand => {
            const getManufacturerInfo = (brand) => {
              switch (brand) {
                case 'IQOS':
                  return { 
                    name: 'Philip Morris', 
                    logo: 'PMI',
                    logoUrl: 'https://www.pmi.com/sites/default/files/2021-05/pmi-logo.png',
                    color: '#2e7d32'
                  };
                case 'Glo':
                  return { 
                    name: 'BAT', 
                    logo: 'BAT',
                    logoUrl: 'https://www.bat.com/sites/default/files/styles/logo/public/2020-01/bat-logo.png',
                    color: '#4caf50'
                  };
                case 'Ploom':
                  return { 
                    name: 'JTI', 
                    logo: 'JTI',
                    logoUrl: 'https://www.jti.com/sites/default/files/2020-01/jti-logo.png',
                    color: '#1976d2'
                  };
                default:
                  return { 
                    name: brand, 
                    logo: brand,
                    logoUrl: null,
                    color: '#666'
                  };
              }
            };
            
            const manufacturer = getManufacturerInfo(brand);
            
            return (
              <Box 
                key={brand}
                sx={{ 
                  flex: 1, 
                  textAlign: 'center',
                  p: 0.5,
                  backgroundColor: manufacturer.color,
                  color: 'white',
                  fontWeight: 'bold',
                  borderRadius: 0.5,
                  mx: 0.25,
                  fontSize: '0.75rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.25
                }}
              >
                {/* Brand Name with Logo */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                    {brand}
                  </Typography>
                  
                  {/* Manufacturer Logo Image */}
                  {manufacturer.logoUrl && (
                    <Box 
                      sx={{ 
                        width: 16, 
                        height: 16, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 0.5,
                        overflow: 'hidden'
                      }}
                    >
                      <img 
                        src={manufacturer.logoUrl} 
                        alt={manufacturer.name}
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100%', 
                          objectFit: 'contain' 
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <Box 
                        sx={{ 
                          display: 'none', 
                          fontSize: '0.5rem', 
                          fontWeight: 'bold',
                          color: '#666'
                        }}
                      >
                        {manufacturer.logo}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
        
        {/* Price Tiers - Ultra Compact */}
        <Box sx={{ pb: 2 }}>
          {priceGroups.slice(0, 8).map((priceGroup, groupIndex) => ( // Limit to 8 price tiers max
          <Box key={groupIndex} sx={{ display: 'flex', mb: 0.5, minHeight: 80 }}>
            {/* Price Label */}
            <Box 
              sx={{ 
                width: 60, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#e0e0e0',
                borderRadius: 0.5,
                mr: 0.25,
                flexShrink: 0
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                {priceGroup.priceIndex}%
              </Typography>
            </Box>
            
            {/* Brand Columns */}
            {allBrands.map(brand => {
              const brandProducts = priceGroup.products.filter(p => p.brand === brand);
              
              return (
                <Box 
                  key={brand}
                  sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0.5,
                    backgroundColor: 'transparent',
                    borderRadius: 0.5,
                    mx: 0.25,
                    minHeight: 60
                  }}
                >
                  {brandProducts.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                      -
                    </Typography>
                  ) : (
                    // Show only the first (most expensive) product per brand per price tier
                    (() => {
                      const product = brandProducts[0]; // Take only the first product
                      return (
                        <Box 
                          onClick={() => handleProductClick(product)}
                          sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            p: 0.25,
                            borderRadius: 0.5,
                            transition: 'all 0.2s ease-in-out',
                            width: '100%',
                            border: referenceProduct && product.title === referenceProduct.title ? '2px solid #ff9800' : 'none',
                            backgroundColor: referenceProduct && product.title === referenceProduct.title ? '#fff3e0' : 'transparent',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                              transform: 'scale(1.05)'
                            }
                          }}
                        >
                          {/* Product Image - Ultra Small */}
                          <Box 
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                              borderRadius: 0.5,
                              mb: 0.25
                            }}
                          >
                            {product.image && product.image !== 'null' && product.image !== '' ? (
                              <img 
                                src={product.image} 
                                alt={product.title}
                                style={{ 
                                  maxWidth: '100%', 
                                  maxHeight: '100%', 
                                  objectFit: 'contain' 
                                }}
                              />
                            ) : (
                              <ImageIcon sx={{ color: '#666', fontSize: 20 }} />
                            )}
                          </Box>
                          
                          {/* Product Name - Full Length */}
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              textAlign: 'center',
                              fontWeight: 'bold',
                              fontSize: '0.6rem',
                              lineHeight: 1.2,
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                              hyphens: 'auto'
                            }}
                          >
                            {product.title || 'Product'}
                          </Typography>
                          
                          {/* Price - Ultra Small */}
                          <Typography 
                            variant="caption" 
                            color="primary"
                            sx={{ fontWeight: 'bold', fontSize: '0.6rem', lineHeight: 1 }}
                          >
                            €{product.displayPrice}
                          </Typography>
                        </Box>
                      );
                    })()
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
        </Box>
      </Paper>
    );
  };
  
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading brand board summary...</Typography>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={loadData} sx={{ mt: 2 }}>
          <Refresh sx={{ mr: 1 }} />
          Retry
        </Button>
      </Box>
    );
  }
  
  const deduplicatedProducts = deduplicateProducts(filteredData);
  
  return (
    <Box sx={{ p: 2, height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Header - Ultra Compact */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
          <Store sx={{ mr: 1, fontSize: 24 }} />
          Brand Board Summary
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selectedCountry.toUpperCase()} - {deduplicatedProducts.length} unique products
        </Typography>
      </Box>
      
      {/* Filters - Ultra Compact */}
      <Box sx={{ mb: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={1}>
            {/* Country Toggle */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Country</InputLabel>
                <Select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                >
                  {availableCountries.map(country => (
                    <MenuItem key={country} value={country}>
                      {country.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Search */}
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 16 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {/* Currency Conversion */}
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={convertToEUR}
                    onChange={(e) => setConvertToEUR(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Convert to EUR"
              />
            </Grid>
            
            {/* Refresh */}
            <Grid item xs={12} sm={3}>
              <Button 
                onClick={loadData} 
                startIcon={<Refresh sx={{ fontSize: 16 }} />}
                size="small"
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      
      {/* Product Groups - Canvas View */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {renderProductGroups()}
      </Box>
      
      {/* Product Detail Dialog */}
      <Dialog 
        open={productDetailDialogOpen} 
        onClose={() => setProductDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Store sx={{ fontSize: 24 }} />
            Product Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Box>
              <Grid container spacing={3}>
                {/* Product Image */}
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageIcon sx={{ fontSize: 20 }} />
                        Product Image
                      </Typography>
                      <Box 
                        component="img"
                        src={selectedProduct.image && selectedProduct.image !== 'null' ? selectedProduct.image : 'https://via.placeholder.com/300x200?text=Product+Image'}
                        alt={selectedProduct.title}
                        sx={{
                          width: '100%',
                          height: 300,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: '1px solid #e0e0e0'
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Product Information */}
                <Grid item xs={12} md={8}>
                  <Card>
                    <CardContent>
                      <Typography variant="h5" gutterBottom sx={{ color: '#1976d2' }}>
                        {selectedProduct.title}
                      </Typography>
                      
                      <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
                        <Chip 
                          label={selectedProduct.brand}
                          color="primary" 
                          variant="outlined"
                        />
                        <Chip 
                          label={selectedProduct.country}
                          color="secondary" 
                          variant="outlined"
                        />
                        <Chip 
                          label={selectedProduct.retailer}
                          color="success" 
                          variant="outlined"
                        />
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Product Characteristics */}
                      <Typography variant="h6" gutterBottom>
                        Product Characteristics
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2">
                            <strong>Category:</strong> {selectedProduct.clean_category || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2">
                            <strong>Flavor:</strong> {selectedProduct.flavor_intensity || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2">
                            <strong>Strength:</strong> {selectedProduct.intensity || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2">
                            <strong>Variant:</strong> {selectedProduct.variant || 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      {/* Pricing Information */}
                      <Typography variant="h6" gutterBottom>
                        Pricing & Availability
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2">
                            <strong>Currency:</strong> {selectedProduct.currency || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2">
                            <strong>Actual Price:</strong> {selectedProduct.actual_price || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2">
                            <strong>Regular Price:</strong> {selectedProduct.regular_price || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2">
                            <strong>Stock:</strong> {selectedProduct.stock_status || 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      {/* Additional Information */}
                      <Typography variant="h6" gutterBottom>
                        Additional Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>Product ID:</strong> {selectedProduct.id || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>Position:</strong> {selectedProduct.position || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>URL:</strong> 
                            {selectedProduct.url ? (
                              <a 
                                href={selectedProduct.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ marginLeft: 8, color: '#1976d2' }}
                              >
                                View Product
                              </a>
                            ) : ' N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Last Updated:</strong> {selectedProduct.updated_at || 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BrandBoardSummaryView;
